import type {
  AppointmentBillingSyncResult,
  BillingLedgerFilter,
  BillingLedgerPage,
  BillingLedgerRow,
  BillingLedgerSummary,
  LedgerStatus,
  ClinicAdvance,
  ClinicReceipt,
  ContactBilling,
  ContactBillingSummary,
  CreateAdvancePayload,
  SyncContactBillingResult,
  UnattachedBilling,
} from "@/features/clinic/types/billing";
import type {
  BillingPatientFilter,
  BillingPatientRow,
  BillingPatientsPage,
  BillingReviewFilter,
  BillingReviewPage,
  BillingReviewRow,
} from "@/features/clinic/types/billing-patients";
import type { ClinicInvoice } from "@/features/clinic/types/invoice";

import { DateTime } from "luxon";

import {
  createId,
  createPastTimestamp,
  newestFirst,
} from "../../generators/base";
import {
  externalAdvanceCode,
  externalReceiptCode,
  nextAdvanceCode,
  nextReceiptCode,
} from "../../generators/billing";
import { nextInvoiceCode } from "../../generators/invoice";
import { useMockStore } from "../../store";
import { CLINICSOFT_MOCK_INTEGRATION } from "../integration";

import { getAppointmentMock } from "./appointments";

import { sumInvoiceAmounts } from "@/features/clinic/helpers/invoice";
import { COLLECTED_WINDOW_DAYS } from "@/features/clinic/constants/billing";
import { APPOINTMENT_STATUS } from "@/features/clinic/types/appointment";
import {
  BILLING_PATIENT_SORT,
  BILLING_PATIENT_VIEW,
  BILLING_REVIEW_KIND,
} from "@/features/clinic/types/billing-patients";
import {
  APPOINTMENT_BILLING_SYNC_STATUS,
  BILLING_SOURCE,
  CONTACT_BILLING_SYNC_STATUS,
  LEDGER_TYPE,
  SETTLEMENT_STATUS,
} from "@/features/clinic/types/billing";
import {
  INVOICE_REVIEW_STATE,
  INVOICE_SOURCE,
  INVOICE_STATUS,
} from "@/features/clinic/types/invoice";
import { PATIENT_MRN_FIELD_KEY } from "@/features/contact/constants/patient";
import { getContactDisplayName } from "@/utils/contact";
import { todayDate } from "@/utils/date";

const sum = (amounts: string[]) =>
  amounts.reduce((total, amount) => total + Number(amount), 0);

const money = (value: number) => Math.max(value, 0).toFixed(2);

/** Mirrors the server's summary rule so mock mode adds up the same way. */
function summarize(
  invoices: ClinicInvoice[],
  receipts: ClinicReceipt[],
  advances: ClinicAdvance[],
): ContactBillingSummary {
  const liveReceipts = receipts.filter(
    (receipt) => receipt.status !== SETTLEMENT_STATUS.VOID,
  );
  const advancesTotal = sum(
    advances
      .filter((advance) => advance.status === SETTLEMENT_STATUS.CLEARED)
      .map((advance) => advance.amount),
  );
  const fromAdvance = sum(
    liveReceipts
      .filter((receipt) => receipt.settledFromAdvance)
      .map((receipt) => receipt.amount),
  );
  const direct = sum(
    liveReceipts
      .filter((receipt) => !receipt.settledFromAdvance)
      .map((receipt) => receipt.amount),
  );
  const invoiced = sumInvoiceAmounts(invoices);
  // Mirrors the server: what is owed is the invoice's own status, not billed
  // minus receipts — a receipt is the patient's copy and the CRM sends one for
  // only some payments.
  const unpaid = sumInvoiceAmounts(
    invoices.filter((invoice) => invoice.status === INVOICE_STATUS.UNPAID),
  );

  return {
    advances: money(advancesTotal),
    fromAdvance: money(fromAdvance),
    direct: money(direct),
    balance: money(advancesTotal - fromAdvance),
    invoiced: money(invoiced),
    outstanding: money(Math.max(unpaid, 0)),
  };
}

const rowsFor = (contactId: string) => {
  const store = useMockStore.getState();

  return {
    invoices: store.invoices.filter((row) => row.contactId === contactId),
    receipts: store.receipts.filter((row) => row.contactId === contactId),
    advances: store.advances.filter((row) => row.contactId === contactId),
  };
};

export function getContactBillingMock(contactId: string): ContactBilling {
  const { invoices, receipts, advances } = rowsFor(contactId);

  return {
    summary: summarize(invoices, receipts, advances),
    invoices: newestFirst(invoices),
    receipts: newestFirst(receipts),
    advances: newestFirst(advances),
  };
}

export function getUnattachedBillingMock(contactId: string): UnattachedBilling {
  const { invoices, receipts, advances } = rowsFor(contactId);

  return {
    invoices: newestFirst(
      invoices.filter(
        (row) =>
          row.appointmentId === null && row.status !== INVOICE_STATUS.VOID,
      ),
    ),
    receipts: newestFirst(
      receipts.filter(
        (row) =>
          row.appointmentId === null && row.status !== SETTLEMENT_STATUS.VOID,
      ),
    ),
    advances: newestFirst(
      advances.filter(
        (row) =>
          row.appointmentId === null && row.status !== SETTLEMENT_STATUS.VOID,
      ),
    ),
  };
}

/**
 * Adds one row of each kind, shaped the way ClinicSoft returns them, and
 * reports one skipped so the toast copy exercises every part of its wording.
 * The real endpoint 404s on an unknown contact, which surfaces as the error
 * alert rather than as a status.
 */
export function syncContactBillingMock(
  contactId: string,
): SyncContactBillingResult {
  const store = useMockStore.getState();
  const contact = store.contacts.find((c) => c.id === contactId);

  if (!contact) throw new Error("Contact not found");

  const existingMrn = contact.dynamicValues?.[PATIENT_MRN_FIELD_KEY];
  const mrn =
    existingMrn !== null && existingMrn !== undefined && String(existingMrn)
      ? String(existingMrn)
      : `${100100 + store.advances.length}`;
  const now = createPastTimestamp(0);
  const today = todayDate();
  const sequence = store.receipts.length + store.advances.length + 1;

  const advance: ClinicAdvance = {
    id: createId(),
    code: nextAdvanceCode(store.advances),
    externalId: `cs-adv-sync-${sequence}`,
    externalCode: externalAdvanceCode(sequence),
    contactId,
    appointmentId: null,
    source: BILLING_SOURCE.CRM,
    integrationId: null,
    amount: "1500.00",
    status: SETTLEMENT_STATUS.CLEARED,
    paymentType: "Deposit",
    advanceDate: today,
    notes: null,
    createdByCrmUser: "Cashier",
    matchedUserId: null,
    createdAt: now,
    updatedAt: null,
  };

  const receipt: ClinicReceipt = {
    id: createId(),
    code: nextReceiptCode(store.receipts),
    externalId: `cs-rec-sync-${sequence}`,
    externalCode: externalReceiptCode(sequence, true),
    contactId,
    appointmentId: null,
    invoiceId: null,
    externalInvoiceId: null,
    externalInvoiceCode: null,
    source: BILLING_SOURCE.CRM,
    integrationId: null,
    amount: "450.00",
    status: SETTLEMENT_STATUS.CLEARED,
    paymentType: "Procedure",
    settledFromAdvance: true,
    receiptDate: today,
    notes: null,
    createdByCrmUser: "Cashier",
    matchedUserId: null,
    createdAt: now,
    updatedAt: null,
  };

  const invoice: ClinicInvoice = {
    id: createId(),
    appointmentId: null,
    contactId,
    code: nextInvoiceCode(store.invoices),
    externalCode: `INV-SALES-${100200 + sequence}`,
    source: INVOICE_SOURCE.CRM,
    integrationId: null,
    invoiceDate: today,
    paymentType: "Procedure",
    createdByCrmUser: "Cashier",
    matchedUserId: null,
    subtotal: "450.00",
    discount: "0.00",
    vat: "0.00",
    total: "450.00",
    status: INVOICE_STATUS.PAID,
    reviewState: INVOICE_REVIEW_STATE.APPLIED,
    paidAt: now,
    notes: null,
    createdByUserId: null,
    organizationId: store.organization?.id ?? "mock-org",
    workspaceId: store.workspaces[0]?.id ?? "mock-workspace",
    createdAt: now,
    updatedAt: null,
  };

  store.addAdvance(advance);
  store.addReceipt(receipt);
  store.addInvoice(invoice);

  return {
    status: CONTACT_BILLING_SYNC_STATUS.SYNCED,
    mrn,
    counts: {
      invoices: 1,
      receipts: 1,
      advances: 1,
      contactsCreated: 0,
      skipped: 1,
    },
    integrationName: CLINICSOFT_MOCK_INTEGRATION.name,
  };
}

/** A deposit banked by hand: cleared, manual, dated as given or today. */
export function createAdvanceMock(
  contactId: string,
  payload: Omit<CreateAdvancePayload, "contactId"> & {
    appointmentId?: string | null;
  },
): ClinicAdvance {
  const store = useMockStore.getState();

  const advance: ClinicAdvance = {
    id: createId(),
    code: nextAdvanceCode(store.advances),
    externalId: null,
    externalCode: null,
    contactId,
    appointmentId: payload.appointmentId ?? null,
    source: BILLING_SOURCE.MANUAL,
    integrationId: null,
    amount: payload.amount.toFixed(2),
    status: SETTLEMENT_STATUS.CLEARED,
    paymentType: payload.paymentType ?? null,
    advanceDate: payload.advanceDate ?? todayDate(),
    notes: payload.notes ?? null,
    createdByCrmUser: null,
    matchedUserId: null,
    createdAt: createPastTimestamp(0),
    updatedAt: null,
  };

  store.addAdvance(advance);

  return advance;
}

/** A receipt written by hand against a visit - a counter payment or a draw on the advance. */
export function createReceiptMock(
  contactId: string,
  payload: {
    appointmentId: string;
    invoiceId?: string | null;
    amount: number;
    settledFromAdvance: boolean;
  },
): ClinicReceipt {
  const store = useMockStore.getState();

  const receipt: ClinicReceipt = {
    id: createId(),
    code: nextReceiptCode(store.receipts),
    externalId: null,
    externalCode: null,
    contactId,
    appointmentId: payload.appointmentId,
    invoiceId: payload.invoiceId ?? null,
    externalInvoiceId: null,
    externalInvoiceCode: null,
    source: BILLING_SOURCE.MANUAL,
    integrationId: null,
    amount: payload.amount.toFixed(2),
    status: SETTLEMENT_STATUS.CLEARED,
    paymentType: null,
    settledFromAdvance: payload.settledFromAdvance,
    receiptDate: todayDate(),
    notes: null,
    createdByCrmUser: null,
    matchedUserId: null,
    createdAt: createPastTimestamp(0),
    updatedAt: null,
  };

  store.addReceipt(receipt);

  return receipt;
}

// The server flags what the viewer may still fix; the mock leaves every payment open.
const changeableInvoices = (appointmentId: string) =>
  newestFirst(
    useMockStore
      .getState()
      .invoices.filter((row) => row.appointmentId === appointmentId),
  ).map((row) => ({ ...row, editable: true }));

/**
 * Per-visit sync: the mock has no CRM to ask, so it reports what is already
 * attached to the appointment and the contact's advance balance, shaped the
 * way the real endpoint answers. An appointment never linked to a CRM visit
 * is `not_linked`, exactly as the server says it.
 */
/** What our records hold for the visit - the sync's shape, nothing pulled. */
export function readAppointmentBillingMock(
  appointmentId: string,
): AppointmentBillingSyncResult {
  const store = useMockStore.getState();
  const appointment = getAppointmentMock(appointmentId);

  if (!appointment) throw new Error("Appointment not found");

  const status = !appointment.contactId
    ? APPOINTMENT_BILLING_SYNC_STATUS.NO_CONTACT
    : !appointment.externalId
      ? APPOINTMENT_BILLING_SYNC_STATUS.NOT_LINKED
      : APPOINTMENT_BILLING_SYNC_STATUS.LOCAL;

  return {
    status,
    integrationName: CLINICSOFT_MOCK_INTEGRATION.name,
    counts: null,
    appointment,
    ledger: {
      invoices: changeableInvoices(appointmentId),
      receipts: newestFirst(
        store.receipts.filter((row) => row.appointmentId === appointmentId),
      ),
      advances: newestFirst(
        store.advances.filter((row) => row.appointmentId === appointmentId),
      ),
    },
    advanceBalance: appointment.contactId
      ? getContactBillingMock(appointment.contactId).summary.balance
      : "0.00",
  };
}

export function syncAppointmentBillingMock(
  appointmentId: string,
): AppointmentBillingSyncResult {
  const store = useMockStore.getState();
  const appointment = getAppointmentMock(appointmentId);

  if (!appointment) throw new Error("Appointment not found");

  const ledger = {
    invoices: changeableInvoices(appointmentId),
    receipts: newestFirst(
      store.receipts.filter((row) => row.appointmentId === appointmentId),
    ),
    advances: newestFirst(
      store.advances.filter((row) => row.appointmentId === appointmentId),
    ),
  };
  const advanceBalance = appointment.contactId
    ? getContactBillingMock(appointment.contactId).summary.balance
    : "0.00";
  const base = {
    integrationName: CLINICSOFT_MOCK_INTEGRATION.name,
    counts: null,
    appointment,
    ledger,
    advanceBalance,
  };

  if (!appointment.contactId) {
    return { ...base, status: APPOINTMENT_BILLING_SYNC_STATUS.NO_CONTACT };
  }
  if (!appointment.externalId) {
    return { ...base, status: APPOINTMENT_BILLING_SYNC_STATUS.NOT_LINKED };
  }

  // The pull is remembered on the row, so the next open shows what is held.
  const billingCheckedAt = new Date().toISOString();

  store.updateAppointment(appointmentId, { billingCheckedAt });

  return {
    ...base,
    appointment: { ...appointment, billingCheckedAt },
    status: APPOINTMENT_BILLING_SYNC_STATUS.SYNCED,
    counts: {
      invoices: 0,
      receipts: 0,
      advances: 0,
      contactsCreated: 0,
      skipped: 0,
    },
  };
}

const ledgerRows = (): BillingLedgerRow[] => {
  const store = useMockStore.getState();
  const nameOf = (contactId: string) => {
    const contact = store.contacts.find((row) => row.id === contactId);

    return contact ? getContactDisplayName(contact) : null;
  };

  const base = (row: ClinicInvoice | ClinicReceipt | ClinicAdvance) => ({
    id: row.id,
    code: row.code,
    externalCode: row.externalCode,
    contactId: row.contactId,
    contactName: nameOf(row.contactId),
    appointmentId: row.appointmentId,
    source: row.source,
    createdAt: row.createdAt,
  });

  return [
    ...store.invoices.map((invoice) => ({
      ...base(invoice),
      kind: LEDGER_TYPE.INVOICE,
      amount: invoice.total,
      status: invoice.status as LedgerStatus,
      date: invoice.invoiceDate,
      detail: invoice.notes ?? invoice.paymentType,
      settledFromAdvance: false,
      isHeld: invoice.reviewState === INVOICE_REVIEW_STATE.HELD,
    })),
    ...store.receipts.map((receipt) => ({
      ...base(receipt),
      kind: LEDGER_TYPE.RECEIPT,
      amount: receipt.amount,
      status: receipt.status as LedgerStatus,
      date: receipt.receiptDate,
      detail: receipt.notes,
      settledFromAdvance: receipt.settledFromAdvance,
      isHeld: false,
    })),
    ...store.advances.map((advance) => ({
      ...base(advance),
      kind: LEDGER_TYPE.ADVANCE,
      amount: advance.amount,
      status: advance.status as LedgerStatus,
      date: advance.advanceDate,
      detail: advance.notes,
      settledFromAdvance: false,
      isHeld: false,
    })),
  ];
};

/** Mirrors the server's rule: void rows and held invoices are not money. */
function summarizeLedger(rows: BillingLedgerRow[]): BillingLedgerSummary {
  const totalOf = (matching: BillingLedgerRow[]) =>
    money(sum(matching.map((row) => row.amount)));

  return {
    invoiced: totalOf(
      rows.filter(
        (row) =>
          row.kind === LEDGER_TYPE.INVOICE &&
          row.status !== INVOICE_STATUS.VOID &&
          !row.isHeld,
      ),
    ),
    collected: totalOf(
      rows.filter(
        (row) =>
          row.kind === LEDGER_TYPE.RECEIPT &&
          row.status === SETTLEMENT_STATUS.CLEARED,
      ),
    ),
    advances: totalOf(
      rows.filter(
        (row) =>
          row.kind === LEDGER_TYPE.ADVANCE &&
          row.status === SETTLEMENT_STATUS.CLEARED,
      ),
    ),
  };
}

/** What the mock desk and CRM have used - the filter's options in mock mode. */
const MOCK_PAYMENT_TYPES = ["Card", "Cash", "Transfer"];

export function getBillingLedgerMock(
  filters: BillingLedgerFilter,
): BillingLedgerPage {
  const page = filters.page ?? 1;
  const limit = filters.limit ?? 25;
  const search = filters.search?.toLowerCase();

  const matched = ledgerRows()
    .filter((row) => {
      if (filters.contactId && row.contactId !== filters.contactId)
        return false;
      if (filters.types?.length && !filters.types.includes(row.kind))
        return false;
      if (filters.statuses?.length && !filters.statuses.includes(row.status))
        return false;
      if (filters.source && row.source !== filters.source) return false;

      const occurredOn = row.date ?? row.createdAt.slice(0, 10);

      if (filters.startDate && occurredOn < filters.startDate) return false;
      if (filters.endDate && occurredOn > filters.endDate) return false;
      if (
        filters.settledFromAdvance !== undefined &&
        row.kind === LEDGER_TYPE.RECEIPT &&
        row.settledFromAdvance !== filters.settledFromAdvance
      )
        return false;
      if (
        search &&
        !`${row.code} ${row.externalCode ?? ""} ${row.contactName ?? ""}`
          .toLowerCase()
          .includes(search)
      ) {
        return false;
      }
      if (
        filters.amountMin !== undefined &&
        Number(row.amount) < filters.amountMin
      )
        return false;
      if (
        filters.amountMax !== undefined &&
        Number(row.amount) > filters.amountMax
      )
        return false;

      return true;
    })
    .sort((a, b) =>
      (b.date ?? b.createdAt).localeCompare(a.date ?? a.createdAt),
    );

  const start = (page - 1) * limit;
  const result = matched.slice(start, start + limit);

  return {
    result,
    meta: {
      count: matched.length,
      currentPage: page,
      previousPage: page > 1 ? page - 1 : null,
      nextPage: start + limit < matched.length ? page + 1 : null,
    },
    summary: summarizeLedger(matched),
    paymentTypes: MOCK_PAYMENT_TYPES,
  };
}

/** The same rule the server follows, so mock mode adds up the same way. */
function patientTotals(contactId: string) {
  const store = useMockStore.getState();

  const invoices = store.invoices.filter(
    (invoice) =>
      invoice.contactId === contactId &&
      invoice.status !== INVOICE_STATUS.VOID &&
      invoice.reviewState !== INVOICE_REVIEW_STATE.HELD,
  );
  const receipts = store.receipts.filter(
    (receipt) =>
      receipt.contactId === contactId &&
      receipt.status === SETTLEMENT_STATUS.CLEARED,
  );
  const advances = store.advances.filter(
    (advance) =>
      advance.contactId === contactId &&
      advance.status === SETTLEMENT_STATUS.CLEARED,
  );

  const unpaid = invoices.filter(
    (invoice) => invoice.status === INVOICE_STATUS.UNPAID,
  );
  const fromAdvance = sum(
    receipts
      .filter((receipt) => receipt.settledFromAdvance)
      .map((r) => r.amount),
  );
  const direct = sum(
    receipts
      .filter((receipt) => !receipt.settledFromAdvance)
      .map((r) => r.amount),
  );
  const dateOf = (row: { date: string | null; createdAt: string }) =>
    row.date ?? row.createdAt.slice(0, 10);

  const happened = [
    ...invoices.map((i) =>
      dateOf({ date: i.invoiceDate, createdAt: i.createdAt }),
    ),
    ...receipts.map((r) =>
      dateOf({ date: r.receiptDate, createdAt: r.createdAt }),
    ),
    ...advances.map((a) =>
      dateOf({ date: a.advanceDate, createdAt: a.createdAt }),
    ),
  ].sort();

  const unpaidDates = unpaid
    .map((invoice) =>
      dateOf({ date: invoice.invoiceDate, createdAt: invoice.createdAt }),
    )
    .sort();

  return {
    hasBilling: invoices.length + receipts.length + advances.length > 0,
    invoiced: money(sum(invoices.map((i) => i.total))),
    outstanding: money(sum(unpaid.map((i) => i.total))),
    balance: money(sum(advances.map((a) => a.amount)) - fromAdvance),
    collected: money(fromAdvance + direct),
    oldestUnpaidDate: unpaidDates[0] ?? null,
    lastBillingDate: happened.at(-1) ?? null,
  };
}

function patientRows(): BillingPatientRow[] {
  const store = useMockStore.getState();

  return store.contacts
    .map((contact): BillingPatientRow | null => {
      const totals = patientTotals(contact.id);

      if (!totals.hasBilling) return null;

      const lastVisit = store.appointments
        .filter(
          (appointment) =>
            appointment.contactId === contact.id &&
            appointment.startDate <= new Date().toISOString(),
        )
        .sort((a, b) => b.startDate.localeCompare(a.startDate))[0];

      return {
        contactId: contact.id,
        contactName: getContactDisplayName(contact),
        phone: contact.phone ?? null,
        invoiced: totals.invoiced,
        outstanding: totals.outstanding,
        balance: totals.balance,
        collected: totals.collected,
        oldestUnpaidDate: totals.oldestUnpaidDate,
        lastBillingDate: totals.lastBillingDate,
        lastVisitAt: lastVisit?.startDate ?? null,
        lastVisitTreatment: lastVisit?.treatment?.name ?? null,
        lastVisitDoctor: lastVisit?.doctor?.name ?? null,
      };
    })
    .filter((row): row is BillingPatientRow => row !== null);
}

function reviewRows(): BillingReviewRow[] {
  const store = useMockStore.getState();
  const nameOf = (contactId: string | null) => {
    const contact = store.contacts.find((row) => row.id === contactId);

    return contact ? getContactDisplayName(contact) : null;
  };

  const held: BillingReviewRow[] = store.invoices
    .filter((invoice) => invoice.reviewState === INVOICE_REVIEW_STATE.HELD)
    .map((invoice) => {
      const duplicate = store.invoices.find(
        (other) =>
          other.id !== invoice.id &&
          other.appointmentId === invoice.appointmentId &&
          other.status !== INVOICE_STATUS.VOID &&
          other.reviewState !== INVOICE_REVIEW_STATE.HELD,
      );

      return {
        id: invoice.id,
        kind: BILLING_REVIEW_KIND.HELD_INVOICE,
        contactId: invoice.contactId,
        contactName: nameOf(invoice.contactId),
        amount: invoice.total,
        code: invoice.code,
        externalCode: invoice.externalCode,
        date: invoice.invoiceDate ?? invoice.createdAt.slice(0, 10),
        duplicateOfCode: duplicate?.code ?? null,
        duplicateOfDate:
          duplicate?.invoiceDate ?? duplicate?.createdAt.slice(0, 10) ?? null,
        appointmentId: invoice.appointmentId,
        treatmentName: null,
        doctorName: null,
      };
    });

  const unbilled: BillingReviewRow[] = store.appointments
    .filter(
      (appointment) =>
        appointment.status === APPOINTMENT_STATUS.COMPLETED &&
        !store.invoices.some(
          (invoice) => invoice.appointmentId === appointment.id,
        ),
    )
    .map((appointment) => ({
      id: appointment.id,
      kind: BILLING_REVIEW_KIND.UNBILLED_VISIT,
      contactId: appointment.contactId,
      contactName: nameOf(appointment.contactId),
      amount: null,
      code: null,
      externalCode: null,
      date: appointment.startDate.slice(0, 10),
      duplicateOfCode: null,
      duplicateOfDate: null,
      appointmentId: appointment.id,
      treatmentName: appointment.treatment?.name ?? null,
      doctorName: appointment.doctor?.name ?? null,
    }));

  return [...held, ...unbilled].sort((a, b) =>
    (b.date ?? "").localeCompare(a.date ?? ""),
  );
}

export function getBillingPatientsMock(
  filters: BillingPatientFilter,
): BillingPatientsPage {
  const page = filters.page ?? 1;
  const limit = filters.limit ?? 25;
  const search = filters.search?.toLowerCase();
  const view = filters.view ?? BILLING_PATIENT_VIEW.OWES;
  const sort = filters.sort ?? BILLING_PATIENT_SORT.OWED;

  const all = patientRows();
  const owes = all.filter((row) => Number(row.outstanding) > 0);
  const credit = all.filter((row) => Number(row.balance) > 0);

  const inView =
    view === BILLING_PATIENT_VIEW.OWES
      ? owes
      : view === BILLING_PATIENT_VIEW.CREDIT
        ? credit
        : all;

  const matched = inView
    .filter((row) => {
      if (!search) return true;

      return `${row.contactName ?? ""} ${row.phone ?? ""}`
        .toLowerCase()
        .includes(search);
    })
    .sort((a, b) => {
      if (sort === BILLING_PATIENT_SORT.OLDEST) {
        return (a.oldestUnpaidDate ?? "9999").localeCompare(
          b.oldestUnpaidDate ?? "9999",
        );
      }

      if (sort === BILLING_PATIENT_SORT.CREDIT) {
        return Number(b.balance) - Number(a.balance);
      }

      if (sort === BILLING_PATIENT_SORT.RECENT) {
        return (b.lastBillingDate ?? "").localeCompare(a.lastBillingDate ?? "");
      }

      return Number(b.outstanding) - Number(a.outstanding);
    });

  const start = (page - 1) * limit;
  const collectedSince = (days: number) =>
    DateTime.now().minus({ days }).toISODate() ?? "";
  const receiptsIn = (from: string, to: string) =>
    useMockStore
      .getState()
      .receipts.filter((receipt) => {
        if (receipt.status !== SETTLEMENT_STATUS.CLEARED) return false;

        const on = receipt.receiptDate ?? receipt.createdAt.slice(0, 10);

        return on > from && on <= to;
      })
      .map((receipt) => receipt.amount);

  const today = todayDate();
  const oldestUnpaid = owes
    .map((row) => row.oldestUnpaidDate)
    .filter((date): date is string => date !== null)
    .sort();

  return {
    result: matched.slice(start, start + limit),
    meta: {
      count: matched.length,
      currentPage: page,
      previousPage: page > 1 ? page - 1 : null,
      nextPage: start + limit < matched.length ? page + 1 : null,
    },
    summary: {
      owed: money(sum(owes.map((row) => row.outstanding))),
      owedPatientCount: owes.length,
      oldestUnpaidDate: oldestUnpaid[0] ?? null,
      collected: money(
        sum(receiptsIn(collectedSince(COLLECTED_WINDOW_DAYS), today)),
      ),
      collectedPrevious: money(
        sum(
          receiptsIn(
            collectedSince(COLLECTED_WINDOW_DAYS * 2),
            collectedSince(COLLECTED_WINDOW_DAYS),
          ),
        ),
      ),
      creditHeld: money(sum(credit.map((row) => row.balance))),
      creditPatientCount: credit.length,
      counts: {
        owes: owes.length,
        credit: credit.length,
        all: all.length,
        review: reviewRows().length,
      },
    },
  };
}

export function getBillingReviewMock(
  filters: BillingReviewFilter,
): BillingReviewPage {
  const page = filters.page ?? 1;
  const limit = filters.limit ?? 25;
  const rows = reviewRows();
  const start = (page - 1) * limit;

  return {
    result: rows.slice(start, start + limit),
    meta: {
      count: rows.length,
      currentPage: page,
      previousPage: page > 1 ? page - 1 : null,
      nextPage: start + limit < rows.length ? page + 1 : null,
    },
  };
}
